type NewRelicAttributeValue = string | number | boolean | null | undefined;
type BrowserAgentInstance = object;

interface NewRelicApi {
  addPageAction?: (name: string, attributes?: Record<string, string | number | boolean>) => void;
  setCustomAttribute?: (name: string, value: string | number | boolean | null, persist?: boolean) => void;
}

interface ConnectivityIncidentState {
  id: string;
  startedAt: number;
  failureCount: number;
  reconnectAttempts: number;
}

declare global {
  interface Window {
    newrelic?: NewRelicApi;
    __achieveApexNewRelicAgent?: BrowserAgentInstance;
    __achieveApexNewRelicInitPromise?: Promise<BrowserAgentInstance | null>;
    __achieveApexConnectivityIncident?: ConnectivityIncidentState | null;
    __achieveApexLastNewRelicEventAt?: Record<string, number>;
    __achieveApexPendingNewRelicAttributes?: Array<{
      name: string;
      value: string | number | boolean | null;
      persist: boolean;
    }>;
    __achieveApexPendingNewRelicPageActions?: Array<{
      actionName: string;
      attributes: Record<string, string | number | boolean>;
    }>;
  }
}

const options = {
  info: {
    applicationID: '1589225107',
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: 'NRJS-090dc50db88fd9faa7b',
    sa: 1,
  },
  init: {
    ajax: {
      deny_list: ['bam.nr-data.net'],
    },
    browser_consent_mode: {
      enabled: false,
    },
    distributed_tracing: {
      enabled: true,
    },
    performance: {
      capture_detail: false,
      capture_marks: false,
      capture_measures: true,
    },
    privacy: {
      cookies_enabled: true,
    },
  },
  loader_config: {
    accountID: '6505376',
    agentID: '1589225107',
    applicationID: '1589225107',
    licenseKey: 'NRJS-090dc50db88fd9faa7b',
    trustKey: '6505376',
  },
};

const THROTTLE_WINDOW_MS = 15000;

const ensurePendingAttributesQueue = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  if (!window.__achieveApexPendingNewRelicAttributes) {
    window.__achieveApexPendingNewRelicAttributes = [];
  }

  return window.__achieveApexPendingNewRelicAttributes;
};

const ensurePendingPageActionsQueue = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  if (!window.__achieveApexPendingNewRelicPageActions) {
    window.__achieveApexPendingNewRelicPageActions = [];
  }

  return window.__achieveApexPendingNewRelicPageActions;
};

const ensureEventCache = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  if (!window.__achieveApexLastNewRelicEventAt) {
    window.__achieveApexLastNewRelicEventAt = {};
  }

  return window.__achieveApexLastNewRelicEventAt;
};

const shouldThrottle = (key: string, throttleMs = THROTTLE_WINDOW_MS) => {
  if (typeof window === 'undefined') {
    return true;
  }

  const cache = ensureEventCache();
  const now = Date.now();
  const lastSentAt = cache[key];

  if (lastSentAt && now - lastSentAt < throttleMs) {
    return true;
  }

  cache[key] = now;
  return false;
};

const sanitizeAttributes = (attributes: Record<string, NewRelicAttributeValue>) => {
  return Object.entries(attributes).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      acc[key] = value;
    }

    return acc;
  }, {});
};

const getNewRelic = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.newrelic ?? null;
};

const flushPendingNewRelicCalls = () => {
  const newRelic = getNewRelic();

  if (!newRelic) {
    return;
  }

  if (newRelic.setCustomAttribute) {
    const pendingAttributes = ensurePendingAttributesQueue();

    while (pendingAttributes.length > 0) {
      const attribute = pendingAttributes.shift();

      if (!attribute) {
        break;
      }

      newRelic.setCustomAttribute(attribute.name, attribute.value, attribute.persist);
    }
  }

  if (newRelic.addPageAction) {
    const pendingPageActions = ensurePendingPageActionsQueue();

    while (pendingPageActions.length > 0) {
      const pageAction = pendingPageActions.shift();

      if (!pageAction) {
        break;
      }

      newRelic.addPageAction(pageAction.actionName, pageAction.attributes);
    }
  }
};

export const initNewRelicBrowser = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (window.__achieveApexNewRelicAgent) {
    flushPendingNewRelicCalls();
    return window.__achieveApexNewRelicAgent;
  }

  if (!window.__achieveApexNewRelicInitPromise) {
    window.__achieveApexNewRelicInitPromise = import('@newrelic/browser-agent/loaders/browser-agent')
      .then(({ BrowserAgent }) => {
        if (!window.__achieveApexNewRelicAgent) {
          window.__achieveApexNewRelicAgent = new BrowserAgent(options);
        }

        flushPendingNewRelicCalls();
        return window.__achieveApexNewRelicAgent;
      })
      .catch(() => null);
  }

  return window.__achieveApexNewRelicAgent ?? null;
};

const getCurrentPath = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location.pathname;
};

const getIncidentState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.__achieveApexConnectivityIncident ?? null;
};

const setIncidentState = (state: ConnectivityIncidentState | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.__achieveApexConnectivityIncident = state;
};

const createIncidentId = () => {
  return `connectivity-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getOrCreateConnectivityIncident = () => {
  const existingIncident = getIncidentState();

  if (existingIncident) {
    return existingIncident;
  }

  const incident: ConnectivityIncidentState = {
    id: createIncidentId(),
    startedAt: Date.now(),
    failureCount: 0,
    reconnectAttempts: 0,
  };

  setIncidentState(incident);
  return incident;
};

export const updateConnectivityIncident = (updater: (incident: ConnectivityIncidentState) => ConnectivityIncidentState) => {
  const incident = getOrCreateConnectivityIncident();
  const nextIncident = updater(incident);
  setIncidentState(nextIncident);
  return nextIncident;
};

export const clearConnectivityIncident = () => {
  const incident = getIncidentState();
  setIncidentState(null);
  return incident;
};

export const setNewRelicAttribute = (name: string, value: NewRelicAttributeValue, persist = false) => {
  initNewRelicBrowser();
  const newRelic = getNewRelic();

  if (value === undefined) {
    return;
  }

  if (!newRelic?.setCustomAttribute) {
    ensurePendingAttributesQueue().push({ name, value, persist });
    return;
  }

  newRelic.setCustomAttribute(name, value, persist);
};

export const setNewRelicPageContext = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const orgId = segments[0] && /^\d+$/.test(segments[0]) ? Number(segments[0]) : undefined;
  const routeGroup = orgId ? segments[1] ?? 'root' : segments[0] ?? 'root';

  setNewRelicAttribute('currentUrlPath', pathname);
  setNewRelicAttribute('routeGroup', routeGroup);
  setNewRelicAttribute('orgId', orgId);
};

export const recordNewRelicPageAction = (
  actionName: string,
  attributes: Record<string, NewRelicAttributeValue> = {},
  throttleKey?: string,
  throttleMs?: number
) => {
  if (typeof window === 'undefined') {
    return;
  }

  initNewRelicBrowser();
  const newRelic = getNewRelic();

  const key = throttleKey ?? actionName;
  if (shouldThrottle(key, throttleMs)) {
    return;
  }

  const mergedAttributes = sanitizeAttributes({
    ...attributes,
    browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    currentUrlPath: getCurrentPath(),
  });

  if (!newRelic?.addPageAction) {
    ensurePendingPageActionsQueue().push({
      actionName,
      attributes: mergedAttributes,
    });
    return;
  }

  newRelic.addPageAction(actionName, mergedAttributes);
};
